import React, { useState, useEffect } from 'react'
import Sidebar from '../components/layout/Sidebar'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Database, Plus, Eye, Edit, Trash2, Loader2, AlertCircle, CheckCircle2, Clock, XCircle, ArrowLeft, Shield, CheckCircle, Key } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { useAuth } from '../contexts/AuthContext'
import { dataSourcesService, DataSource as BackendDataSource } from '../services/dataSourcesService'
import DataSourceSelection from '../components/datasources/DataSourceSelection'
import FirestoreConnectForm from '../components/datasources/FirestoreConnectForm'
import FirestoreWIFSetupForm from '../components/datasources/FirestoreWIFSetupForm'
import GoogleAnalyticsSetupForm from '../components/datasources/GoogleAnalyticsSetupForm'
import TestConnectionDialog from '../components/datasources/TestConnectionDialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

type ViewState = 'list' | 'selection' | 'firestore-form' | 'firestore-method-choice' | 'firestore-wif-form' | 'google-analytics-form'

interface FirestoreConnection {
  connectionName: string
  projectId: string
  serviceAccountKey: string
}

const DataSources: React.FC = () => {
  const [dataSources, setDataSources] = useState<BackendDataSource[]>([]);
  const [currentView, setCurrentView] = useState<ViewState>('list');
  const [selectedDataSourceType, setSelectedDataSourceType] = useState<string | null>(null);
  const [editingDataSource, setEditingDataSource] = useState<BackendDataSource | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [dataSourceToDelete, setDataSourceToDelete] = useState<BackendDataSource | null>(null);
  const [isTesting, setIsTesting] = useState(false);
  const [testingDataSource, setTestingDataSource] = useState<BackendDataSource | null>(null);
  const { toast } = useToast();
  const { currentUser } = useAuth();
  
  // For now, use a default organization ID - replace with actual organization logic
  const organizationId = currentUser?.uid || 'default-org';

  useEffect(() => {
    loadDataSources();
  }, [organizationId]);

  const loadDataSources = async () => {
    try {
      setIsLoading(true);
      const response = await dataSourcesService.getUserDataSources(organizationId);
      
      // Ensure all data sources have proper structure
      const safeDataSources = (response.dataSources || []).map(source => ({
        ...source,
        syncSettings: source.syncSettings || { enabled: false, frequency: 'manual', autoRefreshCharts: false },
        configuration: source.configuration || {},
        tags: source.tags || [],
        usageStats: source.usageStats || { chartsUsingSource: 0, totalQueries: 0, dataVolumeProcessed: 0 },
        permissions: source.permissions || { read: [], write: [], admin: [] }
      }));
      
      setDataSources(safeDataSources);
    } catch (error: any) {
      console.error('Error loading data sources:', error);
      toast({
        title: "Error Loading Data Sources",
        description: error.message || "Failed to load your connected data sources.",
        variant: "destructive",
      });
      setDataSources([]); // Set empty array on error
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectDataSource = (dataSourceId: string) => {
    setSelectedDataSourceType(dataSourceId);
    
    if (dataSourceId === 'firestore') {
      setCurrentView('firestore-method-choice');
    } else if (dataSourceId === 'google-analytics') {
      setCurrentView('google-analytics-form');
    } else {
      // For other data sources, show a coming soon message
      toast({
        title: "Coming Soon",
        description: `${dataSourceId} integration is in development and will be available soon.`,
      });
    }
  };

  const handleFirestoreMethodChoice = (method: 'service-account' | 'wif') => {
    if (method === 'service-account') {
      setCurrentView('firestore-form');
    } else {
      setCurrentView('firestore-wif-form');
    }
  };

  const handleWIFComplete = (connection: any) => {
    toast({
      title: "WIF Connection Completed",
      description: `Successfully set up secure connection to ${connection.projectId}.`,
      variant: "success",
    });
    loadDataSources();
    setCurrentView('list');
  };

  const handleGoogleAnalyticsComplete = (connection: any) => {
    toast({
      title: "Google Analytics Connected",
      description: `Successfully set up connection to GA4 property ${connection.propertyId}.`,
      variant: "success",
    });
    loadDataSources();
    setCurrentView('list');
  };

  const handleFirestoreConnect = async (connection: FirestoreConnection) => {
    try {
      const response = await dataSourcesService.createFirestoreDataSource(
        connection.connectionName,
        connection.projectId,
        connection.serviceAccountKey,
        organizationId,
        {
          description: `Firestore connection to ${connection.projectId}`,
          visibility: 'organization'
        }
      );

      toast({
        title: "Data Source Created",
        description: `Connection "${connection.connectionName}" has been created successfully. Testing connection...`,
        variant: "success",
      });

      // Reload data sources to show the new one
      await loadDataSources();
      setCurrentView('list');
      
    } catch (error: any) {
      console.error('Error creating Firestore connection:', error);
      toast({
        title: "Connection Failed",
        description: error.message || "Failed to create Firestore connection.",
        variant: "destructive",
      });
    }
  };

  const handleTestConnection = async (dataSourceId: string) => {
    // Find the data source being tested
    const dataSource = dataSources.find(ds => ds.id === dataSourceId)
    if (!dataSource) return

    try {
      setIsTesting(true)
      setTestingDataSource(dataSource)
      
      const response = await dataSourcesService.testConnection(dataSourceId);
      
      if (response.success) {
        toast({
          title: "Connection Test Successful",
          description: response.message,
          variant: "success",
        });
        // Reload to get updated status
        await loadDataSources();
      } else {
        toast({
          title: "Connection Test Failed",
          description: response.error || "Connection test failed.",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      console.error('Error testing connection:', error);
      toast({
        title: "Test Failed",
        description: error.message || "Failed to test connection.",
        variant: "destructive",
      });
    } finally {
      setIsTesting(false)
      setTestingDataSource(null)
    }
  };

  const handleDeleteDataSource = async (dataSource: BackendDataSource) => {
    setDataSourceToDelete(dataSource);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteDataSource = async () => {
    if (!dataSourceToDelete) return;

    try {
      await dataSourcesService.deleteDataSource(dataSourceToDelete.id);
      toast({
        title: "Data Source Deleted",
        description: `\"${dataSourceToDelete.name}\" has been deleted successfully.`,
        variant: "success",
      });
      await loadDataSources();
    } catch (error: any) {
      console.error('Error deleting data source:', error);
      toast({
        title: "Delete Failed",
        description: error.message || "Failed to delete data source.",
        variant: "destructive",
      });
    } finally {
      setShowDeleteConfirm(false);
      setDataSourceToDelete(null);
    }
  };

  const handleEditDataSource = (dataSource: BackendDataSource) => {
    setEditingDataSource(dataSource);
    if (dataSource.type === 'firestore') {
      setCurrentView('firestore-form');
    } else if (dataSource.type === 'google-analytics') {
      setCurrentView('google-analytics-form');
    }
  };

  const handleBackToSelection = () => {
    setCurrentView('selection');
    setSelectedDataSourceType(null);
    setEditingDataSource(null);
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setSelectedDataSourceType(null);
    setEditingDataSource(null);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'connected':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'error':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'connected':
        return 'text-green-600';
      case 'pending':
        return 'text-yellow-600';
      case 'error':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const formatLastSync = (lastSync?: Date | string) => {
    if (!lastSync) return 'Never';
    
    try {
      const now = new Date();
      const syncDate = new Date(lastSync);
      
      // Check if date is valid
      if (isNaN(syncDate.getTime())) return 'Never';
      
      const diffMs = now.getTime() - syncDate.getTime();
      const diffMins = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      return `${diffDays}d ago`;
    } catch (error) {
      return 'Never';
    }
  };

  const renderContent = () => {
    switch (currentView) {
      case 'selection':
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={handleBackToList}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Data Sources
              </Button>
            </div>
            <DataSourceSelection onSelectDataSource={handleSelectDataSource} />
          </div>
        );
      
      case 'firestore-method-choice':
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={handleBackToList}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Data Sources
              </Button>
            </div>

            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Database className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold leading-tight text-foreground">Choose Connection Method</h1>
                <p className="text-sm text-muted-foreground">
                  Select how you want to connect to your Firestore database
                </p>
              </div>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              {/* WIF Method - Recommended */}
              <Card className="relative border-primary">
                <div className="absolute -top-2 -right-2">
                  <Badge className="bg-green-500 text-white">Recommended</Badge>
                </div>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5 text-green-500" />
                    Workload Identity Federation
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">
                      <strong>Most Secure:</strong> No service account keys to manage. Uses short-lived, automatically rotated tokens.
                    </p>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-green-500" />
                        No long-lived credentials
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-green-500" />
                        Automatic token rotation
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-green-500" />
                        Minimal permissions
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-green-500" />
                        Complete audit trail
                      </li>
                    </ul>
                  </div>
                  <Button 
                    className="w-full"
                    onClick={() => handleFirestoreMethodChoice('wif')}
                  >
                    <Shield className="h-4 w-4 mr-2" />
                    Use Secure Method
                  </Button>
                </CardContent>
              </Card>

              {/* Service Account Method - Legacy */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Key className="h-5 w-5 text-orange-500" />
                    Service Account Key
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">
                      <strong>Traditional Method:</strong> Uses JSON service account keys. Requires manual key management.
                    </p>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      <li className="flex items-center gap-2">
                        <AlertCircle className="h-3 w-3 text-orange-500" />
                        Manual key rotation required
                      </li>
                      <li className="flex items-center gap-2">
                        <AlertCircle className="h-3 w-3 text-orange-500" />
                        Long-lived credentials
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-green-500" />
                        Quick setup
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-green-500" />
                        Works everywhere
                      </li>
                    </ul>
                  </div>
                  <Button 
                    variant="outline"
                    className="w-full"
                    onClick={() => handleFirestoreMethodChoice('service-account')}
                  >
                    <Key className="h-4 w-4 mr-2" />
                    Use Legacy Method
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        );

      case 'firestore-form':
        return (
          <FirestoreConnectForm 
            onBack={editingDataSource ? handleBackToList : handleBackToSelection}
            onConnect={handleFirestoreConnect}
            organizationId={organizationId}
            editingDataSource={editingDataSource}
          />
        );

      case 'firestore-wif-form':
        return (
          <FirestoreWIFSetupForm 
            onBack={handleBackToSelection}
            onComplete={handleWIFComplete}
            organizationId={organizationId}
          />
        );
      
      case 'google-analytics-form':
        return (
          <GoogleAnalyticsSetupForm 
            onBack={editingDataSource ? handleBackToList : handleBackToSelection}
            onComplete={handleGoogleAnalyticsComplete}
            organizationId={organizationId}
            editingDataSource={editingDataSource}
          />
        );
      
      case 'list':
      default:
        if (isLoading) {
          return (
            <div className="flex items-center justify-center min-h-[50vh]">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2 text-muted-foreground">Loading data sources...</span>
            </div>
          );
        }

        return (
          <div>
            {/* Add New Data Source Button */}
            <div className="mb-6">
              <Button onClick={() => setCurrentView('selection')}>
                <Plus className="mr-2 h-4 w-4" /> Add New Data Source
              </Button>
            </div>

            {/* Data Sources List */}
            {dataSources.length === 0 ? (
              <div className="flex flex-col items-center justify-center min-h-[50vh] text-center border-2 border-dashed border-border rounded-lg p-8">
                <Database className="h-16 w-16 text-muted-foreground/50 mb-4" />
                <h2 className="text-xl font-semibold text-foreground mb-2">No Data Sources Connected Yet</h2>
                <p className="text-muted-foreground mb-6 max-w-md">
                  Start by adding a new data source to bring your external data into FireForge and create dynamic visualizations.
                </p>
                <Button onClick={() => setCurrentView('selection')}>
                  <Plus className="mr-2 h-4 w-4" /> Add Your First Data Source
                </Button>
              </div>
            ) : (
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {dataSources?.map(source => (
                  <Card key={source.id} className="relative">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-lg font-medium">{source.name}</CardTitle>
                      <Database className="h-5 w-5 text-muted-foreground" /> 
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="capitalize">
                            {source.type}
                          </Badge>
                          <Badge variant={source.visibility === 'private' ? 'secondary' : 'default'}>
                            {source.visibility}
                          </Badge>
                        </div>
                        
                        {source.type === 'firestore' && source.configuration && (source.configuration as any)?.projectId && (
                          <div className="text-sm text-muted-foreground">
                            Project: {(source.configuration as any).projectId}
                          </div>
                        )}
                        
                        <div className={`flex items-center gap-2 text-sm font-medium ${getStatusColor(source.status)}`}>
                          {getStatusIcon(source.status)}
                          Status: {source.status}
                        </div>
                        
                        <div className="text-xs text-muted-foreground">
                          Last sync: {formatLastSync(source.syncSettings?.lastSync)}
                        </div>
                        
                        {source.lastError && (
                          <div className="text-xs text-red-600 bg-red-50 p-2 rounded">
                            Error: {source.lastError}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex space-x-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleTestConnection(source.id)}
                        >
                          <Eye className="h-4 w-4 mr-1" /> Test
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleEditDataSource(source)}
                        >
                          <Edit className="h-4 w-4 mr-1" /> Edit
                        </Button>
                        <Button 
                          variant="destructive" 
                          size="sm"
                          onClick={() => handleDeleteDataSource(source)}
                        >
                          <Trash2 className="h-4 w-4 mr-1" /> Delete
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        );
    }
  };

  const getPageTitle = () => {
    switch (currentView) {
      case 'selection':
        return 'Choose Data Source';
      case 'firestore-method-choice':
        return 'Firestore Connection Method';
      case 'firestore-form':
        return editingDataSource ? `Edit ${editingDataSource.name}` : 'Connect to Firestore';
      case 'firestore-wif-form':
        return 'Secure Firestore Connection';
      case 'google-analytics-form':
        return editingDataSource ? `Edit ${editingDataSource.name}` : 'Google Analytics Setup';
      case 'list':
      default:
        return 'Data Sources';
    }
  };

  const getPageDescription = () => {
    switch (currentView) {
      case 'selection':
        return 'Select the type of data source you want to connect to FireForge.';
      case 'firestore-method-choice':
        return 'Choose between traditional service account keys or modern Workload Identity Federation.';
      case 'firestore-form':
        return editingDataSource 
          ? 'Update your Firestore database connection settings.' 
          : 'Configure your Firestore database connection.';
      case 'firestore-wif-form':
        return 'Set up a secure connection using Workload Identity Federation - no service account keys required.';
      case 'google-analytics-form':
        return editingDataSource 
          ? 'Update your Google Analytics 4 property configuration.' 
          : 'Set up Google Analytics connection.';
      case 'list':
      default:
        return 'Connect and manage your external data sources. Connect APIs, databases, and other services to power your charts.';
    }
  };

  return (
    <Sidebar>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border pb-5">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
              <Database className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold leading-tight text-foreground">{getPageTitle()}</h1>
              <p className="mt-2 max-w-4xl text-sm text-muted-foreground">
                {getPageDescription()}
              </p>
            </div>
          </div>
          {currentView === 'list' && (
            <Button onClick={() => setCurrentView('selection')}>
              <Plus className="mr-2 h-4 w-4" /> Add New Data Source
            </Button>
          )}
        </div>

        {/* Dynamic Content */}
        {renderContent()}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the data source
              <strong> {dataSourceToDelete?.name}</strong> and all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDataSourceToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteDataSource} className="bg-red-600 hover:bg-red-700">
              Yes, delete it
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Testing Connection Dialog */}
      <TestConnectionDialog 
        isOpen={isTesting}
        dataSourceName={testingDataSource?.name || ''}
      />
    </Sidebar>
  )
}

export default DataSources 